import React, { type FunctionComponent, useState } from 'react'
import { colors, useBooleanState } from '@retailer-platform/shared-components'
import { useRouteMatch } from 'react-router'
import { useCoreContext } from '../../../../utils/core/RPPCoreContext'
import { URL_APP_ADMIN, URL_APP_DEV_PORTAL } from '../../../../utils/routing/routes'
import { useLocalForageKey } from '../../../../utils/local-storage/useLocalForageKey.hooks'
import { NavContext } from './NavContext'
import { type NavContextValue } from './nav.types'

interface NavProviderProps {}

const navStyleContext = {
  'retailer-scoped': {
    backgroundColor: colors.GRAYSCALE.X70,
    contentColor: colors.GRAYSCALE.X0,
    iconColor: 'systemGrayscale00',
  },
  admin: {
    backgroundColor: colors.GRAYSCALE.X10,
    contentColor: colors.GRAYSCALE.X50,
    iconColor: 'systemGrayscale50',
  },
  'dev-portal': {
    backgroundColor: colors.GRAYSCALE.X0,
    contentColor: colors.GRAYSCALE.X70,
    iconColor: 'systemGrayscale50',
  },
} as const

export type LocalStorageProps = {
  navV2Enabled: boolean
}

export const NavProvider: FunctionComponent<NavProviderProps> = ({ children }) => {
  const [isNavOpen, openNav, closeNav] = useBooleanState(false)
  const [folderControlledByTour, setFolderControlledByTour] = useState('')
  const [isBlockedByModal, setIsBlockedByModal] = useState(false)

  const [navV2RouletteEnabled, setNavV2RouletteEnabled] = useState(false)

  const { data, setData: setNavV2Enabled } = useLocalForageKey<LocalStorageProps>('navV2Enabled')

  // data?.navV2Enabled represents the user's preference (manually chosen via the UI) - if undefined fallback to roulette value "navV2RouletteEnabled"
  const navV2Enabled = data?.navV2Enabled ?? navV2RouletteEnabled

  const [mostRecentClickedRoute, setMostRecentClickedRoute] = useState(null)

  const adminMatch = useRouteMatch(URL_APP_ADMIN)
  const devPortalMatch = useRouteMatch(URL_APP_DEV_PORTAL)
  const getNavType = () => {
    if (adminMatch && navV2Enabled) return 'admin-v2'
    if (adminMatch && !navV2Enabled) return 'admin'
    if (devPortalMatch) return 'dev-portal'
    if (navV2Enabled) return 'retailer-scoped-v2'
    return 'retailer-scoped'
  }
  const navType = getNavType()

  const {
    domainNavigationEntries = {},
    domainNavigationEntriesV2 = {},
    domainAdminNavigationEntries = {},
    domainAdminNavigationEntriesV2 = {},
    domainDevPortalNavigationEntries = {},
  } = useCoreContext()

  const domainEntry = () => {
    if (navType === 'admin-v2') return domainAdminNavigationEntriesV2
    if (navType === 'admin') return domainAdminNavigationEntries
    if (navType === 'dev-portal') return domainDevPortalNavigationEntries
    if (navType === 'retailer-scoped') return domainNavigationEntries
    if (navType === 'retailer-scoped-v2') return domainNavigationEntriesV2
  }

  const navTypeSpecificContext = {
    navigationEntries: domainEntry(),
    ...navStyleContext[navType],
  }

  const contextValue: NavContextValue = {
    isNavOpen,
    openNav,
    closeNav,
    folderControlledByTour,
    setFolderControlledByTour,
    isBlockedByModal,
    setIsBlockedByModal,
    navV2RouletteEnabled,
    setNavV2RouletteEnabled,
    navV2Enabled,
    setNavV2Enabled: enabled => setNavV2Enabled({ navV2Enabled: enabled }),
    mostRecentClickedRoute,
    setMostRecentClickedRoute,
    ...navTypeSpecificContext,
  }

  return <NavContext.Provider value={contextValue}>{children}</NavContext.Provider>
}
